import type {
  PayRunExceptionItem,
  PayRunExceptionsPage,
  PayRunPopulationEmployee,
} from './payRunPopulationApiTypes';

/**
 * Per-employee status for the Employees grid.
 *
 * The column identifies the affected worker. The expanded row retains the full server finding,
 * which is too long for a table cell.
 */

export type PayRunEmployeeStatusTone = 'positive' | 'caution' | 'critical';

export interface PayRunEmployeeStatus {
  /** Short worker identity for the tag; the full finding remains in issues. */
  label: string;
  tone: PayRunEmployeeStatusTone;
  /** Every issue raised against this person, worst first; drives the expanded row. */
  issues: PayRunExceptionItem[];
  /**
   * True when the row has an issue behind it. Transactions alone do not open a row: nearly every
   * employee has them, so counting them made the whole grid expandable and the control stopped
   * pointing at anything. The design opens the rows that need attention.
   */
  expandable: boolean;
}

/**
 * Two vocabularies reach this column, because the exceptions page unions three origins:
 *
 * - `compliance` findings carry `ComplianceSeverity.ToString()` — PascalCase `Warning` /
 *   `CannotStartPay`. These are the only items the API attaches a `personId` to, so in practice
 *   they are the ones this column renders.
 * - `population_input` and `transaction_input` issues use lowercase `blocking` /
 *   `warning` / `review_required`. They carry no personId today, but the severity is still
 *   mapped so this does not quietly mis-rank them if that changes.
 *
 * Matching is case-insensitive rather than listing both spellings of each.
 */
const SEVERITY_RANK: Record<string, number> = {
  cannotstartpay: 0,
  blocking: 0,
  critical: 0,
  warning: 1,
  review_required: 2,
  info: 3,
};

const rank = (severity: string): number => SEVERITY_RANK[severity.toLowerCase()] ?? 4;

const toneOf = (severity: string): PayRunEmployeeStatusTone => {
  const normalised = severity.toLowerCase();
  if (normalised === 'cannotstartpay' || normalised === 'blocking' || normalised === 'critical') return 'critical';
  return normalised === 'info' ? 'positive' : 'caution';
};

/**
 * Fallback wording for a person the grid flags but the readiness evaluation has no message for —
 * exceptions not loaded yet, or an exception carrying no personId to join on. It reports the
 * severity and nothing more, because that is all that is actually known.
 */
const severityOnlyLabel = (severity: string): string =>
  toneOf(severity) === 'critical' ? 'Failed' : '1 issue';

/** Groups the evaluation's items by the person they were raised against. */
export const groupExceptionsByPerson = (
  exceptions: PayRunExceptionsPage | null | undefined,
): Map<string, PayRunExceptionItem[]> => {
  const byPerson = new Map<string, PayRunExceptionItem[]>();
  for (const item of exceptions?.items ?? []) {
    if (!item.personId) continue;
    const existing = byPerson.get(item.personId);
    if (existing) existing.push(item);
    else byPerson.set(item.personId, [item]);
  }
  for (const items of byPerson.values()) items.sort((left, right) => rank(left.severity) - rank(right.severity));
  return byPerson;
};

export const buildPayRunEmployeeStatus = (
  employee: PayRunPopulationEmployee,
  byPerson: Map<string, PayRunExceptionItem[]>,
): PayRunEmployeeStatus => {
  const issues = byPerson.get(employee.personId) ?? [];
  const worst = issues[0];

  if (worst) {
    return {
      label: `${employee.employeeNumber}: ${employee.displayName}${issues.length > 1 ? ` +${issues.length - 1}` : ''}`,
      tone: toneOf(worst.severity),
      issues,
      expandable: true,
    };
  }

  // Flagged by the population projection, but the evaluation gave no message to show. Still
  // worth opening: the transactions are the only account of the pay the flag was raised against.
  if (employee.exceptionSeverity) {
    return {
      label: severityOnlyLabel(employee.exceptionSeverity),
      tone: toneOf(employee.exceptionSeverity),
      issues: [],
      expandable: true,
    };
  }

  return {
    label: 'Clear',
    tone: 'positive',
    issues: [],
    expandable: false,
  };
};

/**
 * The rows "expand all" should open: those the run needs attention on. A clear employee stays
 * shut — their transactions are reachable from their own record, and opening every row at once
 * would bury the ones that matter.
 */
export const expandablePayRunEmployees = (
  employees: PayRunPopulationEmployee[],
  byPerson: Map<string, PayRunExceptionItem[]>,
): PayRunPopulationEmployee[] =>
  employees.filter((employee) => buildPayRunEmployeeStatus(employee, byPerson).expandable);
